import axios, { AxiosError, type AxiosRequestConfig } from 'axios';

export interface ApiErrorBody {
  statusCode: number;
  code: string;
  message: string;
  details?: unknown;
  requestId?: string;
}

/**
 * Origin API без суффикса /api — для служебных маршрутов вроде /health и /ready,
 * они вынесены из префикса. Брать их у своего домена нельзя: nginx админки на
 * любой неизвестный путь отдаёт index.html, и вместо JSON приходит HTML-страница.
 */
export const apiOrigin =
  (import.meta.env['VITE_API_URL'] ?? '/api').replace(/\/api\/?$/, '') || '/';

export const api = axios.create({
  baseURL: import.meta.env['VITE_API_URL'] ?? '/api',
  withCredentials: true, // refresh-токен приходит httpOnly-cookie
  timeout: 20_000,
});

/** Access-токен держим только в памяти вкладки: из localStorage его вытащил бы любой XSS. */
let accessToken: string | null = null;
let onAuthFailure: (() => void) | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function setAuthFailureHandler(handler: (() => void) | null): void {
  onAuthFailure = handler;
}

api.interceptors.request.use((config) => {
  if (accessToken) config.headers.Authorization = `Bearer ${accessToken}`;
  return config;
});

/** Обновляем токен ровно один раз на волну параллельных 401 — иначе гонка ротаций. */
let refreshing: Promise<string> | null = null;

async function refreshAccessToken(): Promise<string> {
  refreshing ??= api
    .post<{ accessToken: string }>('/admin/auth/refresh')
    .then((res) => {
      setAccessToken(res.data.accessToken);
      return res.data.accessToken;
    })
    .finally(() => {
      refreshing = null;
    });

  return refreshing;
}

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<ApiErrorBody>) => {
    const config = error.config as (AxiosRequestConfig & { _retried?: boolean }) | undefined;
    const isAuthCall = config?.url?.includes('/admin/auth/');

    if (error.response?.status === 401 && config && !config._retried && !isAuthCall) {
      config._retried = true;
      try {
        await refreshAccessToken();
        return await api.request(config);
      } catch {
        setAccessToken(null);
        onAuthFailure?.();
      }
    }

    const body = error.response?.data;
    return Promise.reject(
      Object.assign(error, {
        apiCode: body?.code ?? 'NETWORK_ERROR',
        apiMessage: body?.message ?? 'Сервис недоступен',
      }),
    );
  },
);

/** Достаёт человекочитаемое сообщение из ошибки любого происхождения. */
export function errorMessage(error: unknown): string {
  if (error instanceof AxiosError) {
    return error.response?.data?.message ?? 'Сервис недоступен';
  }
  return error instanceof Error ? error.message : 'Неизвестная ошибка';
}

export { refreshAccessToken };
